/**
 * Where each message sits in its runs, so a thread reads the way a chat app reads: one name over a
 * burst from the same person, one clock time under a burst sent in the same minute.
 *
 * There are **two independent groupings** here and they deliberately do not have the same
 * boundaries.
 *
 * - The **author run** is about identity. It breaks when somebody else speaks, and it decides the
 *   name, the avatar, the bubble tail and the spacing — all of which `MessageBubble` already keys
 *   off a single `startsRun` flag.
 * - The **minute run** is about the clock, and it ignores who is speaking entirely. Two people
 *   posting inside one minute produce one timestamp between them, because two identical stamps
 *   stacked on top of each other tell the reader nothing the one below already said.
 *
 * They live in one helper because all three thread screens need both, and three copies of a rule
 * like this is how one of them quietly stops matching the other two.
 */

import { formatTimestamp } from './datetime';

/** The minimum a message has to expose for its place in a run to be decided. */
export interface RunnableMessage {
  id: string;
  authorPersonId: string;
  postedAt: string;
}

export interface MessageRun {
  /** First bubble of an author's run: gets the name, the avatar, the tail and the wider margin. */
  startsRun: boolean;
  /**
   * The rendered timestamp, or `null` when the message below carries an identical one and should
   * be the one to show it.
   */
  time: string | null;
}

/**
 * @param messages Oldest first, which is the order every thread screen renders in.
 * @param breakBeforeId
 *   The message the "New" unread divider is drawn above, when there is one. A separator ends both
 *   runs: a bubble sitting directly under the line has to re-introduce its author, or it reads as
 *   belonging to whatever is above the line — and the stamp above the line has to stay, because the
 *   run it belonged to visibly ended there.
 */
export function messageRuns(
  messages: readonly RunnableMessage[],
  breakBeforeId?: string | null,
): MessageRun[] {
  // Rendered once per message and reused for both the neighbour comparison and the output, so the
  // formatter runs N times rather than 2N.
  const stamps = messages.map((message) => formatTimestamp(message.postedAt));

  return messages.map((message, i) => {
    const previous = messages[i - 1];
    const next = messages[i + 1];

    const startsRun =
      previous === undefined ||
      previous.authorPersonId !== message.authorPersonId ||
      message.id === breakBeforeId;

    // Compare the *rendered* strings rather than the instants. formatTimestamp is minute-precision,
    // so identical output is precisely the condition for "this repeats the one below" — and it
    // stays right across the today / earlier-date / earlier-year branches without this file
    // restating any of them. Two instants 40 seconds apart can straddle a minute boundary and
    // correctly show two different times; two 55 seconds apart inside one minute correctly show
    // one.
    const repeatedBelow =
      next !== undefined && next.id !== breakBeforeId && stamps[i + 1] === stamps[i];

    return { startsRun, time: repeatedBelow ? null : stamps[i] };
  });
}
